import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from './ui/AppText';
import { captureException } from '../services/telemetry';

/**
 * ErrorBoundary global (último recurso, acima dos providers e da navegação).
 * Captura falhas que escapam dos boundaries de rota (ex.: erro em um Provider).
 * O retry remonta TODA a subárvore via bump de `resetKey` — uma re-renderização
 * simples reestouraria a mesma árvore; a chave nova força um mount limpo
 * (providers reidratam, queries refazem). Telas individuais são protegidas por
 * ScreenErrorBoundary, então este caminho é raro.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, resetKey: 0 };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        if (__DEV__) {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
        captureException(error, {
            scope: 'root',
            componentStack: errorInfo?.componentStack,
        });
    }

    handleRetry = () => {
        this.setState((s) => ({ hasError: false, error: null, resetKey: s.resetKey + 1 }));
    };

    render() {
        if (this.state.hasError) {
            return (
                <View style={styles.container}>
                    <Text style={styles.icon}>⚠️</Text>
                    <Text style={styles.title}>Ops! Algo deu errado</Text>
                    <Text style={styles.message}>
                        Ocorreu um erro inesperado. Por favor, tente novamente.
                    </Text>
                    <TouchableOpacity style={styles.button} onPress={this.handleRetry}>
                        <Text style={styles.buttonText}>Tentar Novamente</Text>
                    </TouchableOpacity>
                </View>
            );
        }

        return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#0B0D17',
        padding: 20,
    },
    icon: {
        fontSize: 60,
        marginBottom: 20,
    },
    title: {
        fontSize: 22,
        fontWeight: '700',
        color: '#E8EAED',
        marginBottom: 10,
    },
    message: {
        fontSize: 14,
        color: '#A0A3B1',
        textAlign: 'center',
        marginBottom: 30,
        lineHeight: 20,
    },
    button: {
        backgroundColor: '#5B8FF9',
        paddingVertical: 14,
        paddingHorizontal: 30,
        borderRadius: 10,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
    },
});

export default ErrorBoundary;
